<div class="container-fluid mt-3 mb-5 tender-report">

  <!-- Loading -->
  <div *ngIf="loading" class="text-center py-5 text-muted">
    <i class="fas fa-spinner fa-spin me-2"></i> Loading report&hellip;
  </div>

  <ng-container *ngIf="!loading && report">

    <!-- Header -->
    <div class="row g-3 align-items-end mb-3">
      <div class="col-12 col-lg-7">
        <h5 class="mb-1 fw-bold d-flex align-items-center gap-2 flex-wrap">
          Year-over-Year Report
          <span *ngIf="report.previous_mode === 'virtual'"
                class="badge bg-info-subtle text-info fw-semibold"
                ngbTooltip="Previous-year data is assembled per-location from past awarded contracts (TenderRevision auto-selection)."
                placement="right" style="font-size: 0.7rem;">
            <i class="fas fa-magic me-1"></i> Auto-matched previous
          </span>
          <span *ngIf="report.previous_mode === 'linked'"
                class="badge bg-primary-subtle text-primary fw-semibold"
                style="font-size: 0.7rem;">
            <i class="fas fa-link me-1"></i> Linked previous tender
          </span>
        </h5>
        <div class="text-muted small" *ngIf="report.has_previous">
          Comparing <strong>{{ report.current.tender_name }}</strong>
          vs <strong>{{ report.previous.tender_name }}</strong>
          &middot; All values in <strong>{{ report.currency }}</strong> per <strong>{{ report.units }}</strong>
        </div>
        <div class="text-muted small" *ngIf="!report.has_previous">
          Snapshot of <strong>{{ report.current.tender_name }}</strong>
          &middot; Values in <strong>{{ report.currency }}</strong> per <strong>{{ report.units }}</strong>
        </div>
      </div>

      <div class="col-12 col-lg-5">
        <label class="form-label small text-muted mb-1 fw-semibold">
          Compare against
        </label>
        <ng-select
          [items]="dropdownItems"
          [ngModel]="selectedPreviousUid"
          (ngModelChange)="onPreviousTenderChange($event)"
          bindValue="uid"
          bindLabel="name"
          placeholder="Pick a comparison source&hellip;"
          [clearable]="true"
          [disabled]="loading"
          [searchable]="true"
          notFoundText="No comparison options available">
          <ng-template ng-label-tmp let-item="item">
            <span class="fw-semibold">
              <i class="fas me-1"
                 [ngClass]="{
                   'fa-magic text-info': item.kind === 'auto',
                   'fa-link text-primary': item.kind === 'linked',
                   'fa-file-contract text-muted': item.kind === 'tender'
                 }"></i>
              {{ item.name }}
            </span>
            <small class="text-muted ms-2" *ngIf="item.kind === 'tender' && item.date_contract_start">
              {{ item.date_contract_start | date:'MMM yyyy' }}
              <span *ngIf="item.date_contract_end">&ndash; {{ item.date_contract_end | date:'MMM yyyy' }}</span>
            </small>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <div class="d-flex flex-column">
              <div class="fw-semibold">
                <i class="fas me-1"
                   [ngClass]="{
                     'fa-magic text-info': item.kind === 'auto',
                     'fa-link text-primary': item.kind === 'linked',
                     'fa-file-contract text-muted': item.kind === 'tender'
                   }"></i>
                {{ item.name }}
              </div>
              <small class="text-muted" *ngIf="item.kind === 'auto'">
                Assemble previous-year data per location from your past awarded contracts.
              </small>
              <small class="text-muted" *ngIf="item.kind === 'linked'">
                Use the tender configured as this tender's predecessor.
              </small>
              <small class="text-muted" *ngIf="item.kind === 'tender'">
                <span *ngIf="item.status" class="badge rounded-pill bg-light text-dark me-1">{{ item.status }}</span>
                <ng-container *ngIf="item.date_contract_start">
                  {{ item.date_contract_start | date:'MMM yyyy' }}
                  <span *ngIf="item.date_contract_end">&ndash; {{ item.date_contract_end | date:'MMM yyyy' }}</span>
                </ng-container>
              </small>
            </div>
          </ng-template>
        </ng-select>
        <div class="small text-muted mt-1" *ngIf="!selectedPreviousUid">
          <i class="fas fa-info-circle me-1"></i>
          Pick a comparison source to see year-over-year analysis.
        </div>
      </div>
    </div>

    <!-- Empty state: no comparison selected yet -->
    <div *ngIf="!selectedPreviousUid" class="card border-0 shadow-sm mb-3">
      <div class="card-body text-center py-5">
        <div class="mb-3 text-primary">
          <i class="fas fa-chart-line" style="font-size: 3rem; opacity: 0.4;"></i>
        </div>
        <h5 class="fw-bold mb-2">Pick a comparison source to see year-over-year analysis</h5>
        <p class="text-muted mb-0" *ngIf="dropdownItems.length">
          Use the <strong>Compare against</strong> selector above to choose
          auto-matched previous-year data, a linked predecessor tender, or any specific past tender.
        </p>
        <p class="text-muted mb-0" *ngIf="!dropdownItems.length">
          This is your first tender here &mdash; once next year's tender runs, you'll be able to
          compare it against <strong>{{ report.current.tender_name }}</strong>.
        </p>
      </div>
    </div>

    <div *ngIf="report.fx_failed" class="alert alert-warning mb-3" role="alert">
      <strong>Warning:</strong> {{ fxFailedMessage }}
    </div>

    <!-- Empty state: selection made but backend returned no comparable previous -->
    <div *ngIf="selectedPreviousUid && !report.has_previous" class="card border-0 shadow-sm mb-3">
      <div class="card-body text-center py-5">
        <div class="mb-3 text-warning">
          <i class="fas fa-exclamation-circle" style="font-size: 3rem; opacity: 0.5;"></i>
        </div>
        <h5 class="fw-bold mb-2">No comparable previous data</h5>
        <p class="text-muted mb-0" *ngIf="selectedPreviousUid === 'auto'">
          No past awarded contracts were found to auto-match against this tender's locations.
          Pick a specific past tender instead.
        </p>
        <p class="text-muted mb-0" *ngIf="selectedPreviousUid === 'linked'">
          The linked previous tender has no comparable awarded data.
        </p>
        <p class="text-muted mb-0" *ngIf="selectedPreviousUid !== 'auto' && selectedPreviousUid !== 'linked'">
          The selected tender has no comparable awarded data.
        </p>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="row g-3 mb-3">
      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Weighted avg differential</div>
            <div class="fs-4 fw-bold text-dark">
              <ng-container *ngIf="report.current.kpis.weighted_avg_differential != null; else dashKpi">
                {{ report.current.kpis.weighted_avg_differential | number:'1.4-4' }}
                <small class="text-muted fw-normal">{{ report.currency }}/{{ report.units }}</small>
              </ng-container>
            </div>
            <div *ngIf="report.has_previous && report.deltas.weighted_avg_differential as d" class="small mt-1"
                 [class]="deltaClass(d.absolute, true)">
              <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
              {{ formatDelta(d.absolute, 4) }}
              <span class="text-muted">vs {{ report.previous.kpis.weighted_avg_differential | number:'1.4-4' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Total awarded volume</div>
            <div class="fs-4 fw-bold text-dark">
              {{ report.current.kpis.total_awarded_volume | number:'1.0-0' }}
              <small class="text-muted fw-normal">{{ report.units }}</small>
            </div>
            <div *ngIf="report.has_previous && report.deltas.total_awarded_volume as d" class="small mt-1"
                 [class]="deltaClass(d.percent)">
              <i class="fas" [ngClass]="deltaIcon(d.percent)"></i>
              {{ formatDelta(d.percent, 1) }}%
              <span class="text-muted">vs {{ report.previous.kpis.total_awarded_volume | number:'1.0-0' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Estimated spend</div>
            <div class="fs-4 fw-bold text-dark">
              <ng-container *ngIf="report.current.kpis.estimated_spend != null; else spendMissing">
                {{ report.currency }} {{ report.current.kpis.estimated_spend | number:'1.0-0' }}
              </ng-container>
              <ng-template #spendMissing>
                <span class="text-muted" [class.fs-6]="report.current.spend_sp_withheld"
                      data-testid="classic-yoy-spend-missing">{{ spendMissingText('current') }}</span>
              </ng-template>
            </div>
            <div *ngIf="report.has_previous && report.deltas.estimated_spend as d" class="small mt-1"
                 [class]="deltaClass(d.percent, true)">
              <i class="fas" [ngClass]="deltaIcon(d.percent)"></i>
              {{ formatDelta(d.percent, 1) }}%
              <span class="text-muted">vs {{ spendMissingText('previous') || (report.previous.kpis.estimated_spend | number:'1.0-0') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Suppliers awarded</div>
            <div class="fs-4 fw-bold text-dark">{{ report.current.kpis.suppliers_awarded | number }}</div>
            <div *ngIf="report.has_previous && report.deltas.suppliers_awarded as d" class="small mt-1"
                 [class]="deltaClass(d.absolute)">
              <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
              {{ formatDelta(d.absolute, 0) }}
              <span class="text-muted">vs {{ report.previous.kpis.suppliers_awarded }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Suppliers bid</div>
            <div class="fs-4 fw-bold text-dark">{{ report.current.kpis.supplier_bids_received | number }}</div>
            <div *ngIf="report.has_previous && report.deltas.supplier_bids_received as d"
                 class="small mt-1" [class]="deltaClass(d.absolute)">
              <ng-container *ngIf="d.absolute != null; else naDelta">
                <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
                {{ formatDelta(d.absolute, 0) }}
                <span class="text-muted">vs {{ report.previous.kpis.supplier_bids_received }}</span>
              </ng-container>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Locations awarded</div>
            <div class="fs-4 fw-bold text-dark">
              {{ report.current.kpis.locations_awarded }}
              <small class="text-muted fw-normal">/ {{ report.current.kpis.locations_covered }}</small>
            </div>
            <div *ngIf="report.has_previous && report.deltas.locations_awarded as d" class="small mt-1"
                 [class]="deltaClass(d.absolute)">
              <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
              {{ formatDelta(d.absolute, 0) }}
              <span class="text-muted">vs {{ report.previous.kpis.locations_awarded }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Suppliers invited</div>
            <div class="fs-4 fw-bold text-dark">{{ report.current.kpis.suppliers_invited | number }}</div>
            <div *ngIf="report.has_previous && report.deltas.suppliers_invited as d"
                 class="small mt-1" [class]="deltaClass(d.absolute)">
              <ng-container *ngIf="d.absolute != null; else naDelta">
                <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
                {{ formatDelta(d.absolute, 0) }}
                <span class="text-muted">vs {{ report.previous.kpis.suppliers_invited }}</span>
              </ng-container>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-4 col-xl-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small text-uppercase fw-semibold mb-1">Avg suppliers / location</div>
            <div class="fs-4 fw-bold text-dark">
              <ng-container *ngIf="report.current.kpis.avg_suppliers_per_location != null; else dashKpi">
                {{ report.current.kpis.avg_suppliers_per_location | number:'1.2-2' }}
              </ng-container>
            </div>
            <div *ngIf="report.has_previous && report.deltas.avg_suppliers_per_location as d" class="small mt-1"
                 [class]="deltaClass(d.absolute)">
              <i class="fas" [ngClass]="deltaIcon(d.absolute)"></i>
              {{ formatDelta(d.absolute, 2) }}
              <span class="text-muted">vs {{ report.previous.kpis.avg_suppliers_per_location | number:'1.2-2' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p *ngFor="let note of spendNotes()" class="small text-muted mb-2"
       data-testid="classic-yoy-spend-note">{{ note }}</p>

    <ng-template #dashKpi>
      <span class="text-muted">&mdash;</span>
    </ng-template>

    <ng-template #naDelta>
      <span class="text-muted"><i class="fas fa-minus me-1"></i>Not comparable in auto-matched mode</span>
    </ng-template>

    <!-- YoY visual section -->
    <ng-container *ngIf="report.has_previous">

      <!-- Differential by location -->
      <div class="card border-0 shadow-sm mb-3" *ngIf="differentialChart">
        <div class="card-body">
          <h6 class="fw-bold mb-1">Differential by location &mdash; year over year</h6>
          <div class="text-muted small mb-2">Sorted by largest absolute change. Top 20 shown.</div>
          <apx-chart
            [series]="differentialChart.series"
            [chart]="differentialChart.chart"
            [plotOptions]="differentialChart.plotOptions"
            [colors]="differentialChart.colors"
            [dataLabels]="differentialChart.dataLabels"
            [xaxis]="differentialChart.xaxis"
            [yaxis]="differentialChart.yaxis"
            [legend]="differentialChart.legend"
            [tooltip]="differentialChart.tooltip"
            [fill]="differentialChart.fill"
          ></apx-chart>
        </div>
      </div>

      <!-- Supplier market share donuts -->
      <div class="row g-3 mb-3" *ngIf="donutCurrent && donutPrevious">
        <div class="col-12 col-lg-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-1">Supplier market share &mdash; this tender</h6>
              <div class="text-muted small mb-2">By awarded volume in {{ report.units }}.</div>
              <apx-chart
                [series]="donutCurrent.series"
                [chart]="donutCurrent.chart"
                [labels]="donutCurrent.labels"
                [colors]="donutCurrent.colors"
                [plotOptions]="donutCurrent.plotOptions"
                [dataLabels]="donutCurrent.dataLabels"
                [legend]="donutCurrent.legend"
                [responsive]="donutCurrent.responsive"
                [tooltip]="donutCurrent.tooltip"
              ></apx-chart>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-1">Supplier market share &mdash; previous tender</h6>
              <div class="text-muted small mb-2">{{ report.previous.tender_name }}</div>
              <apx-chart
                [series]="donutPrevious.series"
                [chart]="donutPrevious.chart"
                [labels]="donutPrevious.labels"
                [colors]="donutPrevious.colors"
                [plotOptions]="donutPrevious.plotOptions"
                [dataLabels]="donutPrevious.dataLabels"
                [legend]="donutPrevious.legend"
                [responsive]="donutPrevious.responsive"
                [tooltip]="donutPrevious.tooltip"
              ></apx-chart>
            </div>
          </div>
        </div>
      </div>

      <!-- Net supplier volume change -->
      <div class="card border-0 shadow-sm mb-3" *ngIf="netSupplierChart">
        <div class="card-body">
          <h6 class="fw-bold mb-1">Supplier gainers and losers</h6>
          <div class="text-muted small mb-2">Net change in awarded volume vs the previous tender. Top 15 shown.</div>
          <apx-chart
            [series]="netSupplierChart.series"
            [chart]="netSupplierChart.chart"
            [plotOptions]="netSupplierChart.plotOptions"
            [colors]="netSupplierChart.colors"
            [dataLabels]="netSupplierChart.dataLabels"
            [xaxis]="netSupplierChart.xaxis"
            [yaxis]="netSupplierChart.yaxis"
            [legend]="netSupplierChart.legend"
            [tooltip]="netSupplierChart.tooltip"
            [fill]="netSupplierChart.fill"
          ></apx-chart>
        </div>
      </div>

      <!-- Pricing/spend outliers: price shifts + volume shifts + spend movers -->
      <div class="row g-3 mb-3"
           *ngIf="report.outliers as o"
           [hidden]="!o.price_shifts.length && !o.volume_shifts.length && !o.spend_movers.length">
        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.price_shifts.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-warning">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-warning-subtle text-warning me-2">
                  <i class="fas fa-bolt"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Largest price shifts</div>
                  <div class="text-muted small">Per-location differential moved &gt;2&sigma;</div>
                </div>
                <span class="badge bg-warning-subtle text-warning">{{ o.price_shifts.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let p of o.price_shifts">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ p.iata || p.name }}</span>
                    <span class="outlier-value ms-auto" [class]="deltaClass(p.delta, true)">
                      <i class="fas" [ngClass]="deltaIcon(p.delta)"></i>
                      {{ formatDelta(p.delta, 4) }}
                    </span>
                  </div>
                  <div class="outlier-detail text-muted">
                    {{ p.previous_differential | number:'1.2-4' }} &rarr; {{ p.current_differential | number:'1.2-4' }} {{ report.currency }}/{{ report.units }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.volume_shifts.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-info">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-info-subtle text-info me-2">
                  <i class="fas fa-arrows-alt-v"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Volume shifts</div>
                  <div class="text-muted small">Awarded volume moved &ge;25%</div>
                </div>
                <span class="badge bg-info-subtle text-info">{{ o.volume_shifts.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let v of o.volume_shifts">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ v.iata || v.name }}</span>
                    <span class="outlier-value ms-auto" [class]="deltaClass(v.percent_change)">
                      <i class="fas" [ngClass]="deltaIcon(v.percent_change)"></i>
                      {{ formatDelta(v.percent_change, 1) }}%
                    </span>
                  </div>
                  <div class="outlier-detail text-muted">
                    {{ v.previous_volume | number:'1.0-0' }} &rarr; {{ v.current_volume | number:'1.0-0' }} {{ report.units }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.spend_movers.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-success">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-success-subtle text-success me-2">
                  <i class="fas fa-dollar-sign"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Biggest spend movers</div>
                  <div class="text-muted small">Differential &times; volume, year over year</div>
                </div>
                <span class="badge bg-success-subtle text-success">{{ o.spend_movers.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let s of o.spend_movers">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ s.iata || s.name }}</span>
                    <span class="outlier-value ms-auto" [class]="deltaClass(s.delta, true)">
                      <i class="fas" [ngClass]="deltaIcon(s.delta)"></i>
                      {{ formatDeltaCompact(s.delta) }}
                      <small class="text-muted fw-normal">{{ report.currency }}</small>
                    </span>
                  </div>
                  <div class="outlier-detail text-muted">
                    {{ formatCompact(s.previous_spend) }} &rarr; {{ formatCompact(s.current_spend) }} {{ report.currency }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      <!-- Location competition erosion (YoY bid-count drops) -->
      <div class="row g-3 mb-3"
           *ngIf="report.outliers as o"
           [hidden]="!o.competition_erosion.length">
        <div class="col-12 col-lg-6" *ngIf="o.competition_erosion.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-secondary">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-secondary-subtle text-secondary me-2">
                  <i class="fas fa-chart-line"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Competition erosion</div>
                  <div class="text-muted small">Bid count dropped &ge;2 vs last tender</div>
                </div>
                <span class="badge bg-secondary-subtle text-secondary">{{ o.competition_erosion.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let c of o.competition_erosion">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ c.iata || c.name }}</span>
                    <span class="outlier-value ms-auto text-danger">
                      <i class="fas fa-arrow-down"></i>
                      {{ c.drop }}
                    </span>
                  </div>
                  <div class="outlier-detail text-muted">
                    {{ c.previous_bids }} bids &rarr; {{ c.current_bids }} bids
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Engagement timing: participation funnel + bid-submission timing -->
      <div class="row g-3 mb-3" *ngIf="participationChart || report.current.bid_timing">
        <div class="col-12 col-lg-6" *ngIf="participationChart">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-1">Supplier participation funnel</h6>
              <div class="text-muted small mb-2">Invited &rarr; Submitted bid &rarr; Awarded.</div>
              <apx-chart
                [series]="participationChart.series"
                [chart]="participationChart.chart"
                [plotOptions]="participationChart.plotOptions"
                [colors]="participationChart.colors"
                [dataLabels]="participationChart.dataLabels"
                [xaxis]="participationChart.xaxis"
                [yaxis]="participationChart.yaxis"
                [legend]="participationChart.legend"
                [tooltip]="participationChart.tooltip"
                [fill]="participationChart.fill"
              ></apx-chart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6" *ngIf="report.current.bid_timing as t">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-1">Bid submission timing</h6>
              <div class="text-muted small mb-3">When suppliers submit, relative to round open and close.</div>

              <div class="row g-3">
                <div class="col-6">
                  <div class="text-muted small fw-semibold">Median submission</div>
                  <div class="fs-5 fw-bold">{{ formatHours(t.median_hours_before_close) }}
                    <small class="text-muted fw-normal">before close</small>
                  </div>
                  <div *ngIf="report.has_previous && report.previous?.bid_timing as p" class="small text-muted">
                    was {{ formatHours(p.median_hours_before_close) }}
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-semibold">Last-minute rush</div>
                  <div class="fs-5 fw-bold">{{ t.last_24h_percent | number:'1.0-0' }}%
                    <small class="text-muted fw-normal">in final 24h</small>
                  </div>
                  <div *ngIf="report.has_previous && report.previous?.bid_timing as p" class="small text-muted">
                    was {{ p.last_24h_percent | number:'1.0-0' }}%
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-semibold">Early bids</div>
                  <div class="fs-5 fw-bold">{{ t.first_48h_percent | number:'1.0-0' }}%
                    <small class="text-muted fw-normal">in first 48h</small>
                  </div>
                  <div *ngIf="report.has_previous && report.previous?.bid_timing as p" class="small text-muted">
                    was {{ p.first_48h_percent | number:'1.0-0' }}%
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-semibold">Round window</div>
                  <div class="fs-5 fw-bold">{{ formatHours(t.median_round_duration_hours) }}</div>
                  <div *ngIf="report.has_previous && report.previous?.bid_timing as p" class="small text-muted">
                    was {{ formatHours(p.median_round_duration_hours) }}
                  </div>
                </div>
              </div>

              <div class="small text-muted mt-3">
                Based on {{ t.sample_count }} bid{{ t.sample_count === 1 ? '' : 's' }} with recorded submission timestamps.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Virtual-mode info: shown when participation funnel isn't available -->
      <div class="card border-0 shadow-sm mb-3 bg-light"
           *ngIf="report.previous_mode === 'virtual' && !participationChart">
        <div class="card-body d-flex flex-column justify-content-center text-center py-4">
          <div class="text-info mb-2"><i class="fas fa-magic" style="font-size: 1.5rem;"></i></div>
          <h6 class="fw-bold mb-1">Auto-matched previous-year data</h6>
          <p class="text-muted small mb-0">
            Previous-year values are assembled by matching each location to its
            awarded contract from your past tenders &mdash; so invited/bid supplier
            counts from different historical tenders can't be combined here.
            Supplier awards, volumes and differentials are fully comparable.
          </p>
        </div>
      </div>

      <!-- Outliers detail cards: supplier changes row (2-col) -->
      <div class="row g-3 mb-3" *ngIf="report.outliers as o">
        <div class="col-12 col-lg-6" *ngIf="o.new_suppliers.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-success">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-success-subtle text-success me-2">
                  <i class="fas fa-user-plus"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">New supplier entrants</div>
                  <div class="text-muted small">Awarded this tender, absent last time</div>
                </div>
                <span class="badge bg-success-subtle text-success">{{ o.new_suppliers.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let s of o.new_suppliers">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ s.name }}</span>
                    <span class="outlier-value ms-auto text-success">
                      {{ s.awarded_volume | number:'1.0-0' }}
                      <small class="text-muted fw-normal">{{ report.units }}</small>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6" *ngIf="o.dropout_suppliers.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-danger">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-danger-subtle text-danger me-2">
                  <i class="fas fa-user-minus"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Suppliers that dropped out</div>
                  <div class="text-muted small">Awarded last time, absent this tender</div>
                </div>
                <span class="badge bg-danger-subtle text-danger">{{ o.dropout_suppliers.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let s of o.dropout_suppliers">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ s.name }}</span>
                    <span class="outlier-value ms-auto text-muted">
                      had {{ s.previous_volume | number:'1.0-0' }}
                      <small class="fw-normal">{{ report.units }}</small>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Location roster: newly awarded + dropped coverage + unbid locations -->
      <div class="row g-3 mb-3"
           *ngIf="report.outliers as o"
           [hidden]="!o.newly_awarded_locations.length && !o.dropped_locations.length && !o.unbid_locations.length">
        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.newly_awarded_locations.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-success">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-success-subtle text-success me-2">
                  <i class="fas fa-map-marker-alt"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Newly awarded locations</div>
                  <div class="text-muted small">Awarded this tender, absent last time</div>
                </div>
                <span class="badge bg-success-subtle text-success">{{ o.newly_awarded_locations.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let l of o.newly_awarded_locations">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ l.iata || l.name }}</span>
                    <span class="outlier-value ms-auto text-success">
                      {{ l.awarded_volume | number:'1.0-0' }}
                      <small class="text-muted fw-normal">{{ report.units }}</small>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.dropped_locations.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-danger">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-danger-subtle text-danger me-2">
                  <i class="fas fa-map-marker-alt"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Dropped coverage</div>
                  <div class="text-muted small">Awarded last time, absent this tender</div>
                </div>
                <span class="badge bg-danger-subtle text-danger">{{ o.dropped_locations.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let l of o.dropped_locations">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ l.iata || l.name }}</span>
                    <span class="outlier-value ms-auto text-muted">
                      had {{ l.previous_volume | number:'1.0-0' }}
                      <small class="fw-normal">{{ report.units }}</small>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-xl-4" *ngIf="o.unbid_locations.length">
          <div class="card border-0 shadow-sm h-100 outlier-card outlier-card-danger">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="outlier-icon bg-danger-subtle text-danger me-2">
                  <i class="fas fa-ban"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">Unbid locations</div>
                  <div class="text-muted small">Zero supplier bids received &mdash; missed opportunity</div>
                </div>
                <span class="badge bg-danger-subtle text-danger">{{ o.unbid_locations.length }}</span>
              </div>
              <ul class="list-unstyled outlier-list mb-0">
                <li *ngFor="let u of o.unbid_locations">
                  <div class="d-flex align-items-baseline">
                    <span class="outlier-label">{{ u.iata || u.name }}</span>
                    <span class="outlier-value ms-auto text-muted">
                      {{ u.requested_volume | number:'1.0-0' }}
                      <small class="fw-normal">{{ report.units }} requested</small>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state when all outlier buckets are empty -->
      <div class="card border-0 shadow-sm mb-3"
           *ngIf="report.outliers as o"
           [hidden]="o.price_shifts.length || o.volume_shifts.length || o.spend_movers.length
                  || o.new_suppliers.length || o.dropout_suppliers.length
                  || o.competition_erosion.length || o.unbid_locations.length
                  || o.newly_awarded_locations.length || o.dropped_locations.length">
        <div class="card-body text-center py-4 text-muted">
          <i class="fas fa-check-circle text-success me-1"></i>
          No notable outliers detected &mdash; both tenders look very similar.
        </div>
      </div>

    </ng-container>

  </ng-container>

</div>
